import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const TwoFactorModal = ({ isOpen, onClose, email, onVerify, loading }) => {
  const [code, setCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes

  useEffect(() => {
    if (!isOpen) {
      setCode('');
      setTimeLeft(600);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          toast.error('Koden har utløpt. Vennligst logg inn på nytt.');
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error('Koden må være 6 siffer');
      return;
    }
    await onVerify(code);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-4 rounded-full">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>
          <DialogTitle className="text-2xl text-center">
            <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              To-faktor Autentisering
            </span>
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-center">
            Vi har sendt en 6-sifret kode til <strong className="text-white">{email}</strong>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="space-y-2">
            <Label htmlFor="code" className="text-white">Verifiseringskode</Label>
            <Input
              id="code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              required
              className="bg-gray-800 border-gray-700 text-white text-center text-2xl tracking-widest focus:border-blue-600"
              autoFocus
            />
            <p className="text-sm text-gray-400 text-center">
              Koden utløper om <span className="text-yellow-400 font-semibold">{formatTime(timeLeft)}</span>
            </p>
          </div>

          <div className="flex space-x-3">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
              disabled={loading}
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={loading || code.length !== 6}
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifiserer...
                </>
              ) : (
                'Verifiser'
              )}
            </Button>
          </div>
        </form>

        <div className="mt-4 p-4 bg-blue-900/20 border border-blue-700/30 rounded-lg">
          <p className="text-sm text-gray-300 text-center">
            ℹ️ Finner du ikke e-posten? Sjekk søppelposten din.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorModal;
